import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface MyAvatarData {
  photoUrl: string | null;
  companyLogoUrl: string | null;
  initials: string;
}

function buildInitials(first?: string | null, last?: string | null, email?: string | null): string {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (f || l) return `${f[0] || ""}${l[0] || ""}`.toUpperCase() || "?";
  const e = (email || "").trim();
  return e ? e[0].toUpperCase() : "?";
}

/**
 * Loads the avatar data of the logged-in user once (cached via react-query).
 * Works for every role; roles without a photo field simply fall back to initials.
 */
export function useMyAvatar(): MyAvatarData {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["my-avatar", user?.id],
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("caregiver_profiles")
        .select("public_profile_photo_url, profile_photo_url, company_logo_url, first_name, last_name")
        .eq("user_id", user.id)
        .maybeSingle();
      return data;
    },
  });

  const meta = (user?.user_metadata || {}) as Record<string, string | undefined>;

  return {
    photoUrl: data?.public_profile_photo_url || data?.profile_photo_url || meta.avatar_url || meta.picture || null,
    companyLogoUrl: data?.company_logo_url || null,
    initials: buildInitials(
      data?.first_name || meta.first_name || meta.given_name || (meta.full_name || meta.name || "").split(" ")[0],
      data?.last_name || meta.last_name || meta.family_name || (meta.full_name || meta.name || "").split(" ").slice(1).join(" "),
      user?.email,
    ),
  };
}
